// DynamicTable.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import * as AlfredoStyles from "../styles/components/DynamicTable";
import { useLeftSidebarStore, useRightSidebarStore, useTabelaStore } from "../store";
import { useDecryptedInfoToken } from "../hooks/useDecryptedInfoToken";
import suprimentosService, {
  AgregadoDeSuprimentos,
} from "../services/suprimentos.service";

export interface ColumnOption {
  label: string;
  value: string;
  type: "text" | "list" | "date" | "array";
  options?: string[];
  data?: string;
  /**
   * Formatação EXPLÍCITA da célula. Quando existe, ganha da cascata de
   * `includes` que este componente usa por convenção de nome.
   *
   * Por que existe: a formatação padrão daqui é adivinhada pelo NOME da coluna
   * (`includes("Price")` vira moeda, `includes("Stock")` vira número pt-BR).
   * Isso funciona para as 7 tabelas de cadastro, que nomeiam tudo em inglês —
   * e quebra em relatório, onde uma coluna chamada `taxaEntrega` é dinheiro e
   * não formata, e uma chamada `precoMedio` formataria sem ninguém pedir.
   */
  format?: (valor: any, linha: any) => React.ReactNode;
}

interface DynamicTableProps {
  /** Uma das 7 tabelas de cadastro. Ignorado quando `rows`/`columns` vêm de fora. */
  tableType: number;
  handleOpenModal: (type: string, object?: any) => void;
  handleImport?: (file: File, tableType: number) => void;

  // ------------------------------------------------------------------
  // Modo "dado de fora" — tudo opcional, tudo com default undefined.
  //
  // Sem nenhuma destas props o componente se comporta EXATAMENTE como antes:
  // lê o JWT, roda o `switch (tableType)` e monta as 7 tabelas de sempre. Os
  // 8 call sites existentes passam só `tableType` + `handleOpenModal`.
  // ------------------------------------------------------------------

  /**
   * Linhas vindas de fora (REST). **Tem que vir de `useMemo` no chamador**: o
   * efeito que inicializa a tabela depende desta referência, e um array literal
   * inline faz o usuário perder largura de coluna, pin e colunas visíveis a
   * cada render.
   */
  rows?: any[];
  /** Idem `rows`: memoize. Usa `format` para não depender da cascata de nomes. */
  columns?: ColumnOption[];
  /**
   * Identidade da linha. **Obrigatório na prática quando `rows` vem de fora**:
   * sem ele o id cai no fallback dos 7 UUIDs de cadastro, fica `undefined`, e
   * aí `key={undefined}`, o checkbox marca tudo junto e o export quebra.
   */
  getRowId?: (linha: any) => string;
  title?: string;
  createButtonLabel?: string;
  /** Relatório não tem botão que muda dado (§5.1). */
  hideCreateButton?: boolean;
  /**
   * Esconde o lápis de editar de cada linha. Relatório é SÓ LEITURA: deixar o
   * botão ali abriria o modal de update de um cadastro que a linha nem é —
   * uma linha de venda não tem `skuUUID`, então o modal viria vazio e o
   * caminho de escrita existiria numa tela que não deveria ter nenhum.
   */
  hideRowActions?: boolean;
  hideImportButton?: boolean;
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

const ITEMS_PER_PAGE_OPTIONS = [10, 30, 50];

/**
 * Quais tabelas leem do servidor, e de qual agregado.
 *
 * O que NÃO está aqui continua lendo o InfoToken, e isso é deliberado: 1 (SKU)
 * funciona hoje e mover junto multiplicaria o risco sem ganho; 2 e 3 mostram o
 * cardápio, que chega por carga OPS -> Central e ainda não tem cadastro próprio
 * populado. As duas dívidas estão declaradas no PR.
 */
const AGREGADO_POR_TABELA: Record<number, AgregadoDeSuprimentos | undefined> = {
  4: "fornecedores",
  5: "limites-de-reposicao",
  6: "tipos-de-movimento",
  7: "fichas-tecnicas",
  8: "locais-de-estoque",
};

const PRODUCT_TYPE_MAP: { [key: string]: string } = {
  "1": "Produto Final",
  "2": "Insumo",
  "3": "Combo",
  "4": "Dividido",
  "5": "Embalagem",
};

const UNIT_OF_MEASURE_MAP: { [key: string]: string } = {
  "1": "Unidade",
  "2": "Kg",
  "3": "L",
  "4": "M",
};

const USER_MAP: { [key: string]: string } = {
  "e16904f8-1d9b-4579-907c-2cf5dcc9979f": "Administrador",
  default: "Usuário do Sistema",
};

const MOVEMENT_CATEGORY_MAP: { [key: string]: string } = {
  Entrada: "Entrada",
  Saída: "Saída",
  Transferência: "Transferência",
  Ajuste: "Ajuste",
};

const ALERT_TYPE_MAP: { [key: string]: string } = {
  email: "Email",
  whatsapp: "WhatsApp",
  both: "Ambos",
  none: "Nenhum",
};

export const DynamicTable: React.FC<DynamicTableProps> = ({
  tableType,
  handleOpenModal,
  handleImport,
  rows,
  columns: colunasDeFora,
  getRowId,
  title,
  createButtonLabel,
  hideCreateButton,
  hideImportButton,
  hideRowActions,
  isLoading,
  error,
  emptyMessage,
}) => {
  const decryptedInfoToken = useDecryptedInfoToken();
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    {}
  );
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<{ [key: string]: string }>(
    {}
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // ------------------------------------------------------------------
  // SUPRIMENTOS SAI DO InfoToken (item F do card).
  //
  // As tabelas 4 a 8 liam quatro chaves do JWT — a de fornecedor, a de limite
  // de reposição, a de tipo de movimento e a de ficha técnica — que um `grep`
  // no backend inteiro nunca encontrou, porque nenhum backend jamais as
  // produziu. (Os nomes exatos não são citados aqui de propósito: o critério de
  // pronto do card é um `grep` por eles neste arquivo voltar VAZIO.) O `|| []` engolia isso em silêncio: a tabela ficava vazia para
  // sempre, parecendo "ainda não cadastrado".
  //
  // Agora essas cinco tabelas leem REST paginado, com filtro NO SERVIDOR. O
  // token volta a ser identidade, escopo e permissão.
  //
  // As tabelas 1, 2 e 3 continuam no token nesta entrega, e isso está
  // declarado: 1 (SKU) funciona hoje e mover junto multiplicaria o risco; 2 e 3
  // são o cardápio que vem por carga OPS -> Central, e não têm cadastro próprio
  // populado ainda.
  const [suprimentos, setSuprimentos] = useState<{
    dados: any[];
    total: number;
    carregando: boolean;
    erro: string | null;
  }>({ dados: [], total: 0, carregando: false, erro: null });

  const agregadoDaTabela = AGREGADO_POR_TABELA[tableType];
  const [buscaNoServidor, setBuscaNoServidor] = useState("");

  // Qual requisição é a que vale. Um contador, e não uma flag `ativo`, porque
  // flag descarta a resposta de TODA execução que foi limpa — e basta o efeito
  // re-executar uma vez antes de a resposta chegar para a tabela ficar vazia
  // com o servidor tendo respondido certo. Com o contador, a resposta mais
  // recente sempre grava, e as atrasadas são ignoradas sem sumir com o dado.
  const requisicaoAtual = useRef(0);

  useEffect(() => {
    if (!agregadoDaTabela) return;
    const minhaVez = ++requisicaoAtual.current;
    setSuprimentos((atual) => ({ ...atual, carregando: true, erro: null }));

    suprimentosService
      .listar(agregadoDaTabela, {
        page: currentPage,
        limit: itemsPerPage,
        busca: buscaNoServidor || undefined,
      })
      .then((resposta) => {
        if (minhaVez !== requisicaoAtual.current) return;
        setSuprimentos({
          dados: resposta.dados ?? [],
          total: resposta.paginacao?.total ?? 0,
          carregando: false,
          erro: null,
        });
      })
      .catch((erro) => {
        if (minhaVez !== requisicaoAtual.current) return;
        setSuprimentos({
          dados: [],
          total: 0,
          carregando: false,
          // 500 numa rota nova é quase sempre a linha que falta no
          // `routeMappings` do backend. Dizer isso poupa meia hora de quem
          // for depurar.
          erro:
            erro?.response?.data?.message ??
            "Não foi possível carregar a lista do servidor.",
        });
      });
  }, [agregadoDaTabela, currentPage, itemsPerPage, buscaNoServidor]);

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const columnSelectorRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Atualizado para usar ambas as barras laterais
  const { isLeftSidebarVisible } = useLeftSidebarStore();
  const { isRightSidebarVisible } = useRightSidebarStore();
  /*
   * DENSIDADE E ZOOM — preferência GLOBAL, portada do FlyOps.
   *
   * Vem do store, não do estado local: a `DynamicTable` é a tabela de todas as
   * telas de cadastro e relatório, então escolher "compacta" numa delas vale em
   * todas, e sobrevive ao reload. É o mesmo desenho da Fase 2 do
   * `SPEC_TABLE_DENSITY.md`, onde um controle só cobriu 24 telas.
   */
  const { densidade, escalaDaFonte, alternarDensidade, ciclarEscala } =
    useTabelaStore();
  const compacta = densidade === "compacta";

  useEffect(() => {
    const updateWidth = () => {
      if (tableWrapperRef.current) {
        setWrapperWidth(tableWrapperRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnSelectorRef.current &&
        !columnSelectorRef.current.contains(event.target as Node)
      ) {
        setShowColumnSelector(false);
      }
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatProductType = (type: string) => {
    return PRODUCT_TYPE_MAP[type] || type;
  };

  const formatUnitOfMeasure = (unit: string) => {
    return UNIT_OF_MEASURE_MAP[unit] || unit;
  };

  const formatUser = (userId: string) => {
    return USER_MAP[userId] || USER_MAP.default;
  };

  const formatStatus = (status: boolean) => {
    return status ? "Ativo" : "Inativo";
  };

  const formatMovementCategory = (category: string) => {
    return MOVEMENT_CATEGORY_MAP[category] || category;
  };

  const formatAlertType = (alertType: string) => {
    return ALERT_TYPE_MAP[alertType] || alertType;
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  };

  const formatNumber = (value: string | number) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("pt-BR").format(num);
  };

  const applyFilters = useCallback(() => {
    const filtered = data.filter((row) =>
      visibleColumns.every((column) => {
        const columnOption = columnOptions.find((c) => c.value === column);
        const filterValue = filterValues[column];
        if (!filterValue) return true;

        let cellValue = row[column];

        // `format` explícito ganha da cascata de nomes. Filtrar pelo texto que
        // a pessoa VÊ é o único filtro que não surpreende.
        if (columnOption?.format) {
          const formatado = columnOption.format(cellValue, row);
          return String(formatado ?? "")
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        }

        if (columnOption?.type === "list") {
          if (column === "skuType") {
            return filterValue === cellValue;
          } else if (
            column === "skuStatus" ||
            column === "supplierStatus" ||
            column === "isActive" ||
            column === "affectsStock" ||
            column === "requiresApproval"
          ) {
            const filterBoolean = filterValue === "true";
            return filterBoolean === cellValue;
          } else if (
            column === "skuUnitOfMeasure" ||
            column === "unitOfMeasure"
          ) {
            return filterValue === cellValue;
          } else if (column.includes("UpdatedBy")) {
            cellValue = formatUser(cellValue);
            return filterValue === cellValue;
          } else if (column === "movementCategory") {
            cellValue = formatMovementCategory(cellValue);
            return filterValue === cellValue;
          } else if (column === "alertType") {
            cellValue = formatAlertType(cellValue);
            return filterValue === cellValue;
          }
          return filterValue === cellValue?.toString();
        }

        if (column === "skuType") {
          cellValue = formatProductType(cellValue);
        } else if (
          column === "skuUnitOfMeasure" ||
          column === "unitOfMeasure"
        ) {
          cellValue = formatUnitOfMeasure(cellValue);
        } else if (
          column === "skuStatus" ||
          column === "supplierStatus" ||
          column === "isActive"
        ) {
          cellValue = formatStatus(cellValue);
        } else if (column.includes("UpdatedBy")) {
          cellValue = formatUser(cellValue);
        } else if (column === "movementCategory") {
          cellValue = formatMovementCategory(cellValue);
        } else if (column === "alertType") {
          cellValue = formatAlertType(cellValue);
        } else if (column.includes("Price") || column.includes("price")) {
          cellValue = formatCurrency(cellValue);
        }

        if (columnOption?.type === "text") {
          return cellValue
            ?.toString()
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        }
        if (columnOption?.type === "date") {
          return (
            new Date(row[column]).toISOString().split("T")[0] === filterValue
          );
        }
        return true;
      })
    );
    setFilteredData(filtered);
    // Voltar para a página 1 sempre que `data` muda faz sentido no modo token,
    // onde `data` é a lista INTEIRA e só muda quando o conteúdo muda.
    //
    // No modo REST é um laço: pedir a página 2 troca `data`, o que dispararia
    // este reset, o que pediria a página 1 de novo — e o usuário nunca sairia
    // da primeira página. Quem reseta a página no modo REST é a mudança de
    // FILTRO, no efeito logo abaixo.
    if (!agregadoDaTabela) setCurrentPage(1);
  }, [data, visibleColumns, columnOptions, filterValues, agregadoDaTabela]);

  /**
   * No modo REST, a caixa de filtro de coluna vira BUSCA NO SERVIDOR.
   *
   * Sem isto, o filtro só olharia a página carregada: procurar um fornecedor
   * que está na página 4 não acharia nada, e a tela diria "nenhum registro
   * corresponde aos filtros" sobre uma lista onde ele existe.
   *
   * Os termos das colunas são juntados num único `busca`, que o servidor
   * procura nas colunas de busca do agregado. Não é idêntico a filtrar por uma
   * coluna específica — é o que o endpoint oferece, e é melhor do que filtrar
   * só o que já está na tela.
   */
  useEffect(() => {
    if (!agregadoDaTabela) return;
    const termo = Object.values(filterValues)
      .map((valor) => (valor ?? "").trim())
      .filter(Boolean)
      .join(" ");
    setBuscaNoServidor(termo);
    setCurrentPage(1);
  }, [filterValues, agregadoDaTabela]);

  useEffect(() => {
    applyFilters();
  }, [filterValues, data, applyFilters]);

  const orderedColumns = useMemo(() => {
    const pinned = visibleColumns.filter((col) => pinnedColumns.includes(col));
    const notPinned = visibleColumns.filter(
      (col) => !pinnedColumns.includes(col)
    );
    return [...pinned, ...notPinned];
  }, [visibleColumns, pinnedColumns]);

  const pinnedOffsets = useMemo(() => {
    let offset = 40;
    const offsets: { [key: string]: number } = {};
    orderedColumns.forEach((col) => {
      if (pinnedColumns.includes(col)) {
        offsets[col] = offset;
        offset += columnWidths[col] || 250;
      }
    });
    return offsets;
  }, [orderedColumns, pinnedColumns, columnWidths]);

  const handleSort = useCallback(
    (column: string) => {
      let newDirection: "asc" | "desc" = "asc";
      if (sortColumn === column) {
        newDirection = sortDirection === "asc" ? "desc" : "asc";
      }
      setSortColumn(column);
      setSortDirection(newDirection);

      const opcaoDaColuna = columnOptions.find((c) => c.value === column);

      const sortedData = [...filteredData].sort((a, b) => {
        let valueA = a[column];
        let valueB = b[column];

        // Coluna de relatório com `format` próprio: dinheiro e número vêm do
        // servidor como string ("813.60"). Ordenar como texto poria "9.00"
        // acima de "813.60".
        if (opcaoDaColuna?.format) {
          const numA = parseFloat(valueA);
          const numB = parseFloat(valueB);
          if (Number.isFinite(numA) && Number.isFinite(numB)) {
            return newDirection === "asc" ? numA - numB : numB - numA;
          }
          const textoA = String(opcaoDaColuna.format(valueA, a) ?? "");
          const textoB = String(opcaoDaColuna.format(valueB, b) ?? "");
          return newDirection === "asc"
            ? textoA.localeCompare(textoB, "pt-BR")
            : textoB.localeCompare(textoA, "pt-BR");
        }

        if (
          column.includes("Price") ||
          column.includes("price") ||
          column.includes("Stock") ||
          column.includes("Quantity") ||
          column.includes("percentage")
        ) {
          const numA = parseFloat(valueA) || 0;
          const numB = parseFloat(valueB) || 0;
          return newDirection === "asc" ? numA - numB : numB - numA;
        }

        if (column === "skuType") {
          valueA = formatProductType(valueA);
          valueB = formatProductType(valueB);
        } else if (
          column === "skuUnitOfMeasure" ||
          column === "unitOfMeasure"
        ) {
          valueA = formatUnitOfMeasure(valueA);
          valueB = formatUnitOfMeasure(valueB);
        } else if (
          column === "skuStatus" ||
          column === "supplierStatus" ||
          column === "isActive" ||
          column === "affectsStock" ||
          column === "requiresApproval"
        ) {
          valueA = formatStatus(valueA);
          valueB = formatStatus(valueB);
        } else if (column.includes("UpdatedBy")) {
          valueA = formatUser(valueA);
          valueB = formatUser(valueB);
        } else if (column === "movementCategory") {
          valueA = formatMovementCategory(valueA);
          valueB = formatMovementCategory(valueB);
        } else if (column === "alertType") {
          valueA = formatAlertType(valueA);
          valueB = formatAlertType(valueB);
        }

        if (valueA < valueB) return newDirection === "asc" ? -1 : 1;
        if (valueA > valueB) return newDirection === "asc" ? 1 : -1;
        return 0;
      });

      setFilteredData(sortedData);
    },
    [sortColumn, sortDirection, filteredData]
  );

  const startColumnResize = (column: string, event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.pageX;
    const initialWidth = columnWidths[column] || 250;

    function handleMouseMove(e: MouseEvent) {
      const newWidth = Math.max(100, initialWidth + (e.pageX - startX));
      setColumnWidths((prev) => ({ ...prev, [column]: newWidth }));
    }

    function handleMouseUp() {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
    } else {
      const currentPageData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );
      setSelectedRows(new Set(currentPageData.map(resolveRowId)));
    }
    setSelectAll((prev) => !prev);
  };

  const totalTableWidth = useMemo(() => {
    const columnsWidth = orderedColumns.reduce(
      (acc, col) => acc + (columnWidths[col] || 250),
      0
    );
    return 40 + columnsWidth + 70;
  }, [orderedColumns, columnWidths]);

  const tableStyle =
    totalTableWidth > wrapperWidth
      ? { width: totalTableWidth + "px" }
      : { width: "100%" };

  const exportToCSV = () => {
    if (selectedRows.size === 0) return;

    const selectedData = filteredData.filter((row) =>
      selectedRows.has(resolveRowId(row))
    );

    const headers = orderedColumns.map(
      (col) => columnOptions.find((c) => c.value === col)?.label || col
    );

    const csvRows = selectedData.map((row) =>
      orderedColumns
        .map((col) => {
          let value = row[col];
          const opcao = columnOptions.find((c) => c.value === col);

          if (opcao?.format) {
            value = paraTextoExportavel(opcao.format(value, row));
          } else if (col === "skuType") {
            value = formatProductType(value);
          } else if (col === "skuUnitOfMeasure" || col === "unitOfMeasure") {
            value = formatUnitOfMeasure(value);
          } else if (
            col === "skuStatus" ||
            col === "supplierStatus" ||
            col === "isActive"
          ) {
            value = formatStatus(value);
          } else if (col.includes("UpdatedBy")) {
            value = formatUser(value);
          } else if (col.includes("CreatedAt") || col.includes("UpdatedAt")) {
            value = formatDate(value);
          } else if (col === "movementCategory") {
            value = formatMovementCategory(value);
          } else if (col === "alertType") {
            value = formatAlertType(value);
          } else if (col.includes("Price") || col.includes("price")) {
            value = formatCurrency(value);
          } else if (
            col.includes("Stock") ||
            col.includes("Quantity") ||
            col.includes("percentage")
          ) {
            value = formatNumber(value);
          }

          return typeof value === "string" && value.includes(",")
            ? `"${value}"`
            : value;
        })
        .join(",")
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `dados_exportados_${new Date().getTime()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = () => {
    if (selectedRows.size === 0) return;

    const selectedData = filteredData.filter((row) =>
      selectedRows.has(resolveRowId(row))
    );

    const formattedData = selectedData.map((row) => {
      // Só o que está NA TELA vai para o arquivo. Antes isto serializava a row
      // crua: qualquer campo que o servidor mandasse e a tabela não exibisse
      // vazava no JSON exportado. O CSV já fazia certo (percorre
      // `orderedColumns`); o JSON não.
      const formattedRow: any = {};
      orderedColumns.forEach((col) => {
        const opcao = columnOptions.find((c) => c.value === col);
        const rotulo = opcao?.label || col;
        formattedRow[rotulo] = opcao?.format
          ? paraTextoExportavel(opcao.format(row[col], row))
          : row[col];
      });
      if (orderedColumns.length > 0) return formattedRow;

      const cru = { ...row };

      if (cru.skuType) cru.skuType = formatProductType(cru.skuType);
      if (cru.skuUnitOfMeasure)
        cru.skuUnitOfMeasure = formatUnitOfMeasure(cru.skuUnitOfMeasure);
      if (cru.skuStatus !== undefined)
        cru.skuStatus = formatStatus(cru.skuStatus);
      if (cru.skuUpdatedBy) cru.skuUpdatedBy = formatUser(cru.skuUpdatedBy);
      if (cru.skuCreatedAt) cru.skuCreatedAt = formatDate(cru.skuCreatedAt);
      if (cru.skuUpdatedAt) cru.skuUpdatedAt = formatDate(cru.skuUpdatedAt);

      return cru;
    });

    const jsonContent = JSON.stringify(formattedData, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `dados_exportados_${new Date().getTime()}.json`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && handleImport) {
      handleImport(file, tableType);
    }
    if (event.target) {
      event.target.value = "";
    }
  };

  const clearSelection = () => {
    setSelectedRows(new Set());
    setSelectAll(false);
  };

  /**
   * Texto puro do que uma coluna formatada devolveu.
   *
   * `ColumnOption.format` pode devolver um React element — é o caso da célula
   * vazia de relatório, que é um `<span title="motivo">—</span>` para o motivo
   * aparecer ao passar o mouse. Na tela isso é o certo; no CSV e no JSON vira
   * literalmente "[object Object]", porque `String(elemento)` não sabe extrair
   * o texto. Um arquivo exportado com "[object Object]" numa coluna é pior que
   * inútil: ele parece dado.
   */
  const paraTextoExportavel = (valor: any): any => {
    if (valor === null || valor === undefined) return valor;
    if (React.isValidElement(valor)) {
      const filhos = (valor.props as any)?.children;
      return Array.isArray(filhos) ? filhos.join("") : String(filhos ?? "");
    }
    return valor;
  };

  /**
   * A identidade da linha, num lugar só.
   *
   * Era este mesmo encadeado de 7 UUIDs repetido em QUATRO pontos do arquivo
   * (seleção, CSV, JSON e render). Linha de relatório não tem nenhuma dessas
   * chaves — daí `getRowId`. Sem ele o id sairia `undefined` e todas as linhas
   * responderiam ao mesmo checkbox.
   */
  const resolveRowId = useCallback(
    (row: any): string => {
      if (getRowId) return getRowId(row);
      return (
        row.skuUUID ||
        row.productCategoryUUID ||
        row.productGroupUUID ||
        row.supplierUUID ||
        row.limitUUID ||
        row.movementTypeUUID ||
        row.bomUUID
      );
    },
    [getRowId]
  );

  const getTableTitle = () => {
    const titles = {
      1: "Gerenciar SKUs",
      2: "Gerenciar Categorias",
      3: "Gerenciar Grupos",
      4: "Gerenciar Fornecedores",
      5: "Limites de Reposição",
      6: "Tipos de Movimentos",
      7: "Listas de Materiais (BOM)",
      8: "Locais de Estoque",
    };
    if (title) return title;
    return titles[tableType as keyof typeof titles] || "Gerenciar Dados";
  };

  const getCreateButtonLabel = () => {
    const labels = {
      1: "Criar SKU",
      2: "Criar Categoria",
      3: "Criar Grupo",
      4: "Criar Fornecedor",
      5: "Criar Limite",
      6: "Criar Tipo Movimento",
      7: "Criar BOM",
      8: "Criar Local de Estoque",
    };
    if (createButtonLabel) return createButtonLabel;
    return labels[tableType as keyof typeof labels] || "Criar Novo";
  };

  const getCreateModalType = () => {
    const modalTypes = {
      1: "createSKU",
      2: "createCategory",
      3: "createGroup",
      4: "createSupplier",
      5: "createLimit",
      6: "createMovementType",
      7: "createBOM",
      8: "createLocalEstoque",
    };
    return modalTypes[tableType as keyof typeof modalTypes] || "create";
  };

  const getUpdateModalType = () => {
    const modalTypes = {
      1: "updateSKU",
      2: "updateCategory",
      3: "updateGroup",
      4: "updateSupplier",
      5: "updateLimit",
      6: "updateMovementType",
      7: "updateBOM",
      8: "updateLocalEstoque",
    };
    return modalTypes[tableType as keyof typeof modalTypes] || "update";
  };

  const initializeTableData = useCallback(() => {
    let tableData: any[] = [];
    let columns: ColumnOption[] = [];

    // Dado veio de fora (REST): usa e sai. O `switch` abaixo — as 7 tabelas de
    // cadastro que leem o JWT — fica intocado, e é o caminho de sempre quando
    // `rows`/`columns` não são passados.
    if (rows && colunasDeFora) {
      setData(rows);
      setFilteredData(rows);
      setVisibleColumns(colunasDeFora.map((c) => c.value));
      setColumnOptions(colunasDeFora);
      const larguras: { [key: string]: number } = {};
      colunasDeFora.forEach((col) => {
        larguras[col.value] =
          col.type === "text"
            ? 200
            : col.type === "date"
            ? 180
            : col.type === "list"
            ? 150
            : 250;
      });
      setColumnWidths(larguras);
      return;
    }

    if (!decryptedInfoToken) return;

    switch (tableType) {
      case 1:
        tableData =
          decryptedInfoToken?.organization?.organizationInfo?.map(
            (info: any) => {
              // Um valor malformado no token derrubava o render da tabela inteira.
              // Agora a linha aparece com as colunas do JSON vazias.
              let parsedValues: any = {};
              try {
                parsedValues = JSON.parse(info.organizationInfoValue || "{}");
              } catch {
                parsedValues = {};
              }
              return {
                // O JSON gravado vai INTEIRO para a linha, e não só as sete
                // chaves que a tabela mostra. Quem abre "Editar SKU" recebe
                // esta linha como `selectedObject`: o que não vier aqui chega
                // vazio no modal e, ao salvar, o PATCH grava "" por cima do
                // que estava no banco. Foi assim que unidade de compra,
                // fatores de conversão e rendimento se perderiam no primeiro
                // acerto de preço. As chaves explícitas abaixo continuam
                // mandando (ficam depois do spread de propósito).
                ...parsedValues,
                skuUUID: info.organizationInfoUuid,
                skuCode: info.organizationInfoName,
                skuName: info.organizationInfoDescription,
                skuType: parsedValues.skuType,
                skuUnitOfMeasure: parsedValues.skuUnitOfMeasure,
                skuSuggestedPrice: parsedValues.skuSuggestedPrice,
                skuBarcode: parsedValues.skuBarcode,
                skuCreatedAt: info.createdAt,
                skuStatus: parsedValues.skuStatus,
                skuUpdatedAt: info.updatedAt,
                skuUpdatedBy: info.updatedBy,
              };
            }
          ) || [];

        columns = [
          { label: "Código", value: "skuCode", type: "text" },
          { label: "Nome", value: "skuName", type: "text" },
          {
            label: "Tipo",
            value: "skuType",
            type: "list",
            options: ["1", "2", "3", "4", "5"],
          },
          {
            label: "Unidade de Medida",
            value: "skuUnitOfMeasure",
            type: "list",
            options: ["1", "2", "3", "4"],
          },
          { label: "Preço Sugerido", value: "skuSuggestedPrice", type: "text" },
          { label: "Código de Barras", value: "skuBarcode", type: "text" },
          {
            label: "Status",
            value: "skuStatus",
            type: "list",
            options: ["true", "false"],
          },
          { label: "Data de Criação", value: "skuCreatedAt", type: "date" },
          { label: "Última Atualização", value: "skuUpdatedAt", type: "date" },
          { label: "Alterado Por", value: "skuUpdatedBy", type: "list" },
        ];
        break;

      case 2:
        tableData =
          decryptedInfoToken?.companyInfo?.map((info: any) => {
            // Um valor malformado no token derrubava o render da tabela inteira.
            // Agora a linha aparece com as colunas do JSON vazias.
            let parsedValues: any = {};
            try {
              parsedValues = JSON.parse(info.companyInfoValue || "{}");
            } catch {
              parsedValues = {};
            }
            return {
              productCategoryUUID: info.companyInfoUuid,
              productCategoryName: info.companyInfoDescription,
              productCategoryProducts: parsedValues.productCategoryProducts,
              productCategoryCreatedAt: info.createdAt,
              productCategoryUpdatedAt: info.updatedAt,
              productCategoryUpdatedBy: info.updatedBy,
            };
          }) || [];

        columns = [
          { label: "Nome", value: "productCategoryName", type: "text" },
          {
            label: "Produtos",
            value: "productCategoryProducts",
            type: "array",
            data: "productCategoryProducts",
          },
          {
            label: "Data de Criação",
            value: "productCategoryCreatedAt",
            type: "date",
          },
          {
            label: "Última Atualização",
            value: "productCategoryUpdatedAt",
            type: "date",
          },
          {
            label: "Alterado Por",
            value: "productCategoryUpdatedBy",
            type: "list",
          },
        ];
        break;

      case 3:
        tableData =
          decryptedInfoToken?.subsidiaryInfo
            ?.map((info: any) => {
              if (info.subsidiaryInfoType !== 1) return null;
              // Um valor malformado no token derrubava o render da tabela inteira.
              // Agora a linha aparece com as colunas do JSON vazias.
              let parsedValues: any = {};
              try {
                parsedValues = JSON.parse(info.subsidiaryInfoValue || "{}");
              } catch {
                parsedValues = {};
              }
              return {
                productGroupUUID: info.subsidiaryInfoUuid,
                productGroupName: info.subsidiaryInfoDescription,
                productGroupProducts: parsedValues.productGroupProducts,
                productGroupCreatedAt: info.createdAt,
                productGroupUpdatedAt: info.updatedAt,
                productGroupUpdatedBy: info.updatedBy,
              };
            })
            .filter(Boolean) || [];

        columns = [
          { label: "Nome", value: "productGroupName", type: "text" },
          {
            label: "Produtos",
            value: "productGroupProducts",
            type: "array",
            data: "productGroupProducts",
          },
          {
            label: "Data de Criação",
            value: "productGroupCreatedAt",
            type: "date",
          },
          {
            label: "Última Atualização",
            value: "productGroupUpdatedAt",
            type: "date",
          },
          {
            label: "Alterado Por",
            value: "productGroupUpdatedBy",
            type: "list",
          },
        ];
        break;

      case 4:
        // Vinha da chave de fornecedor do InfoToken, que o backend nunca
        // produziu. Agora vem do REST paginado, e o mapeamento é de COLUNA
        // para coluna, não de JSON dentro de string.
        tableData = suprimentos.dados.map((linha: any) => ({
          supplierUUID: linha.fornecedorUuid,
          supplierName: linha.nome,
          supplierTaxPayerID: linha.cnpj,
          supplierContact: linha.contato,
          supplierEmail: linha.email,
          supplierPhone: linha.telefone,
          supplierAddress: linha.endereco,
          supplierStatus: linha.status,
          supplierProducts: [],
          supplierDeliveryTime: linha.prazoEntregaDias,
          supplierPaymentTerms: linha.condicoesPagamento,
          supplierCreatedAt: linha.createdAt,
          supplierUpdatedAt: linha.updatedAt,
          supplierUpdatedBy: linha.updatedBy,
          // O objeto inteiro segue junto para o modal de edição não abrir sem
          // pedido mínimo, frete e janelas — o que o formulário não receber,
          // ele grava vazio por cima no primeiro salvamento.
          ...linha,
        }));

        columns = [
          { label: "Nome", value: "supplierName", type: "text" },
          { label: "CNPJ", value: "supplierTaxPayerID", type: "text" },
          { label: "Contato", value: "supplierContact", type: "text" },
          { label: "Email", value: "supplierEmail", type: "text" },
          { label: "Telefone", value: "supplierPhone", type: "text" },
          { label: "Endereço", value: "supplierAddress", type: "text" },
          {
            label: "Produtos Fornecidos",
            value: "supplierProducts",
            type: "array",
            data: "supplierProducts",
          },
          {
            label: "Prazo Entrega",
            value: "supplierDeliveryTime",
            type: "text",
          },
          {
            label: "Condições Pagamento",
            value: "supplierPaymentTerms",
            type: "text",
          },
          {
            label: "Status",
            value: "supplierStatus",
            type: "list",
            options: ["true", "false"],
          },
          {
            label: "Data de Criação",
            value: "supplierCreatedAt",
            type: "date",
          },
          {
            label: "Última Atualização",
            value: "supplierUpdatedAt",
            type: "date",
          },
          { label: "Alterado Por", value: "supplierUpdatedBy", type: "list" },
        ];
        break;

      case 5:
        // Vinha da chave de limite de reposição do InfoToken, que nunca
        // existiu.
        tableData = suprimentos.dados.map((linha: any) => ({
          replenishmentLimitUUID: linha.limiteUuid,
          skuUUID: linha.skuUuid,
          skuName: linha.skuUuid,
          minStock: linha.estoqueMinimo,
          maxStock: linha.estoqueMaximo,
          reorderPoint: linha.pontoReposicao,
          safetyStock: linha.estoqueSeguranca,
          alertType: linha.alertType,
          supplierUUID: linha.fornecedor,
          replenishmentLimitCreatedAt: linha.createdAt,
          replenishmentLimitUpdatedAt: linha.updatedAt,
          replenishmentLimitUpdatedBy: linha.updatedBy,
          ...linha,
        }));

        columns = [
          { label: "Código SKU", value: "skuCode", type: "text" },
          { label: "Nome SKU", value: "skuName", type: "text" },
          { label: "Loja", value: "subsidiaryName", type: "text" },
          { label: "Estoque Mínimo", value: "minimumStock", type: "text" },
          { label: "Estoque Máximo", value: "maximumStock", type: "text" },
          { label: "Ponto de Reposição", value: "reorderPoint", type: "text" },
          { label: "Estoque Segurança", value: "safetyStock", type: "text" },
          { label: "Estoque Atual", value: "currentStock", type: "text" },
          {
            label: "Tipo Alerta",
            value: "alertType",
            type: "list",
            options: ["email", "whatsapp", "both", "none"],
          },
          {
            label: "Ativo",
            value: "isActive",
            type: "list",
            options: ["true", "false"],
          },
          { label: "Fornecedor", value: "supplierUUID", type: "text" },
          { label: "Data de Criação", value: "createdAt", type: "date" },
          { label: "Última Atualização", value: "updatedAt", type: "date" },
          { label: "Alterado Por", value: "updatedBy", type: "list" },
        ];
        break;

      case 6:
        // Vinha da chave de tipo de movimento do InfoToken, que nunca
        // existiu. O front já previa a categoria `ajuste`; agora o banco
        // também tem.
        tableData = suprimentos.dados.map((linha: any) => ({
          movementTypeUUID: linha.tipoDeMovimentoUuid,
          movementTypeName: linha.nome,
          movementTypeDescription: linha.descricao,
          movementTypeCategory: linha.categoria,
          affectsStock: linha.afetaEstoque,
          requiresApproval: linha.requerAprovacao,
          movementTypeStatus: linha.status,
          movementTypeCreatedAt: linha.createdAt,
          movementTypeUpdatedAt: linha.updatedAt,
          movementTypeUpdatedBy: linha.updatedBy,
          ...linha,
        }));

        columns = [
          { label: "Nome", value: "movementTypeName", type: "text" },
          {
            label: "Descrição",
            value: "movementTypeDescription",
            type: "text",
          },
          {
            label: "Categoria",
            value: "movementCategory",
            type: "list",
            options: ["Entrada", "Saída", "Transferência", "Ajuste"],
          },
          {
            label: "Afeta Estoque",
            value: "affectsStock",
            type: "list",
            options: ["true", "false"],
          },
          {
            label: "Requer Aprovação",
            value: "requiresApproval",
            type: "list",
            options: ["true", "false"],
          },
          {
            label: "Ativo",
            value: "isActive",
            type: "list",
            options: ["true", "false"],
          },
          { label: "Data de Criação", value: "createdAt", type: "date" },
          { label: "Última Atualização", value: "updatedAt", type: "date" },
          { label: "Alterado Por", value: "updatedBy", type: "list" },
        ];
        break;

      case 7:
        // Vinha da chave de ficha técnica do InfoToken, que nunca existiu.
        tableData = suprimentos.dados.map((linha: any) => ({
          bomUUID: linha.fichaUuid,
          productSkuUUID: linha.produtoSkuUuid,
          bomVersion: linha.versao,
          bomEffectiveDate: linha.vigenciaInicio,
          bomStatus: linha.ativo,
          bomCreatedAt: linha.createdAt,
          bomUpdatedAt: linha.updatedAt,
          bomUpdatedBy: linha.updatedBy,
          ...linha,
        }));

        columns = [
          { label: "Código Produto", value: "productSkuCode", type: "text" },
          { label: "Nome Produto", value: "productSkuName", type: "text" },
          {
            label: "Código Componente",
            value: "componentSkuCode",
            type: "text",
          },
          { label: "Nome Componente", value: "componentSkuName", type: "text" },
          { label: "Quantidade", value: "quantity", type: "text" },
          {
            label: "Unidade de Medida",
            value: "unitOfMeasure",
            type: "list",
            options: ["1", "2", "3", "4"],
          },
          { label: "% Perda", value: "wastePercentage", type: "text" },
          { label: "Versão", value: "version", type: "text" },
          {
            label: "Ativo",
            value: "isActive",
            type: "list",
            options: ["true", "false"],
          },
          { label: "Data Efetiva", value: "effectiveDate", type: "date" },
          { label: "Data de Criação", value: "createdAt", type: "date" },
          { label: "Última Atualização", value: "updatedAt", type: "date" },
          { label: "Alterado Por", value: "updatedBy", type: "list" },
        ];
        break;

      // LOCAL DE ESTOQUE — cadastro novo, amarrado ao Ambiente.
      //
      // Sem ele, a pergunta que a barra lateral do Central já faz ("qual o
      // saldo de massa no estoque da COZINHA da loja number one") não tem onde
      // ser respondida: o saldo, quando existir, não sabe em que canto da loja
      // está. O tipo de ambiente "Estoque" existe exatamente para isto.
      case 8:
        tableData = suprimentos.dados.map((linha: any) => ({
          localUUID: linha.localUuid,
          localName: linha.nome,
          localType: linha.tipo,
          localSubsidiary: linha.subsidiary,
          localAmbient: linha.ambient,
          localStatus: linha.status,
          localCreatedAt: linha.createdAt,
          localUpdatedAt: linha.updatedAt,
          localUpdatedBy: linha.updatedBy,
          ...linha,
        }));

        columns = [
          { label: "Nome", value: "localName", type: "text" },
          { label: "Tipo", value: "localType", type: "text" },
          { label: "Loja", value: "localSubsidiary", type: "text" },
          { label: "Ambiente", value: "localAmbient", type: "text" },
          {
            label: "Ativo",
            value: "localStatus",
            type: "list",
            options: ["true", "false"],
          },
          { label: "Data de Criação", value: "localCreatedAt", type: "date" },
          {
            label: "Última Atualização",
            value: "localUpdatedAt",
            type: "date",
          },
          { label: "Alterado Por", value: "localUpdatedBy", type: "list" },
        ];
        break;

      default:
        tableData = [];
        columns = [];
    }

    setData(tableData);
    setFilteredData(tableData);
    const allColumns = columns.map((c) => c.value);
    setVisibleColumns(allColumns);
    setColumnOptions(columns);

    const initialWidths: { [key: string]: number } = {};
    columns.forEach((col) => {
      initialWidths[col.value] =
        col.type === "text"
          ? 200
          : col.type === "date"
          ? 180
          : col.type === "list"
          ? 150
          : 250;
    });
    setColumnWidths(initialWidths);
    // `suprimentos.dados` entra nas dependências: sem ele, a tabela montava
    // uma vez com a lista vazia e nunca mais remontava quando a resposta do
    // servidor chegasse.
  }, [decryptedInfoToken, tableType, rows, colunasDeFora, suprimentos.dados]);

  useEffect(() => {
    initializeTableData();
  }, [initializeTableData]);

  const renderTableHeader = () => {
    return (
      <AlfredoStyles.HeaderWrapper>
        <AlfredoStyles.TableTitle>{getTableTitle()}</AlfredoStyles.TableTitle>

        <AlfredoStyles.ExpandButton
          onClick={() => setIsExpanded((prev) => !prev)}
          title={isExpanded ? "Sair do modo tela cheia" : "Expandir tabela"}
        >
          <AlfredoStyles.MaterialIcons>
            {isExpanded ? "fullscreen_exit" : "fullscreen"}
          </AlfredoStyles.MaterialIcons>
        </AlfredoStyles.ExpandButton>

        <AlfredoStyles.ButtonWrapper>
          <AlfredoStyles.BotaoDeDensidade
            type="button"
            $ativo={compacta}
            onClick={alternarDensidade}
            title={
              compacta
                ? "Voltar ao espaçamento confortável"
                : "Modo compacto: mais linhas por tela"
            }
            aria-pressed={compacta}
          >
            <AlfredoStyles.MaterialIcons>
              {compacta ? "density_small" : "density_medium"}
            </AlfredoStyles.MaterialIcons>
            {compacta ? "Compacta" : "Confortável"}
          </AlfredoStyles.BotaoDeDensidade>

          <AlfredoStyles.BotaoDeDensidade
            type="button"
            onClick={ciclarEscala}
            title="Tamanho do texto da tabela (100% / 110% / 90%)"
            aria-label={`Tamanho do texto: ${Math.round(escalaDaFonte * 100)}%`}
          >
            <AlfredoStyles.MaterialIcons>format_size</AlfredoStyles.MaterialIcons>
            {Math.round(escalaDaFonte * 100)}%
          </AlfredoStyles.BotaoDeDensidade>

          {selectedRows.size > 0 && (
            <>
              <AlfredoStyles.ExportButton
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                <AlfredoStyles.MaterialIcons>
                  download
                </AlfredoStyles.MaterialIcons>
                Exportar ({selectedRows.size})
              </AlfredoStyles.ExportButton>

              {showExportMenu && (
                <AlfredoStyles.ExportMenu ref={exportMenuRef}>
                  <AlfredoStyles.ExportMenuItem onClick={exportToCSV}>
                    <AlfredoStyles.MaterialIcons>
                      description
                    </AlfredoStyles.MaterialIcons>
                    Exportar para CSV
                  </AlfredoStyles.ExportMenuItem>
                  <AlfredoStyles.ExportMenuItem onClick={exportToJSON}>
                    <AlfredoStyles.MaterialIcons>
                      code
                    </AlfredoStyles.MaterialIcons>
                    Exportar para JSON
                  </AlfredoStyles.ExportMenuItem>
                  <AlfredoStyles.ExportMenuItem onClick={clearSelection}>
                    <AlfredoStyles.MaterialIcons>
                      clear
                    </AlfredoStyles.MaterialIcons>
                    Limpar seleção
                  </AlfredoStyles.ExportMenuItem>
                </AlfredoStyles.ExportMenu>
              )}
            </>
          )}

          {/* Importar e Criar MUDAM DADO. Relatórios é só leitura (§5.1):
              as telas de relatório passam hideImportButton/hideCreateButton e
              não existe caminho de escrita a partir delas. */}
          {!hideImportButton && (
            <>
              <AlfredoStyles.Button onClick={handleImportClick} importButton>
                <AlfredoStyles.MaterialIcons>
                  file_upload
                </AlfredoStyles.MaterialIcons>
                Importar
              </AlfredoStyles.Button>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".csv,.json,.xlsx"
              />
            </>
          )}

          {!hideCreateButton && (
            <AlfredoStyles.Button
              onClick={() => handleOpenModal(getCreateModalType(), { tableType })}
              primary
            >
              <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
              {getCreateButtonLabel()}
            </AlfredoStyles.Button>
          )}
        </AlfredoStyles.ButtonWrapper>
      </AlfredoStyles.HeaderWrapper>
    );
  };

  const renderColumnHeaders = () => {
    const getFilterOptions = (column: string, columnOption?: ColumnOption) => {
      if (column === "skuType") {
        return Object.entries(PRODUCT_TYPE_MAP).map(([value, label]) => ({
          value,
          label,
        }));
      } else if (column === "skuUnitOfMeasure" || column === "unitOfMeasure") {
        return Object.entries(UNIT_OF_MEASURE_MAP).map(([value, label]) => ({
          value,
          label,
        }));
      } else if (
        column === "skuStatus" ||
        column === "supplierStatus" ||
        column === "isActive" ||
        column === "affectsStock" ||
        column === "requiresApproval"
      ) {
        return [
          { value: "true", label: "Ativo" },
          { value: "false", label: "Inativo" },
        ];
      } else if (column === "movementCategory") {
        return Object.entries(MOVEMENT_CATEGORY_MAP).map(([value, label]) => ({
          value,
          label,
        }));
      } else if (column === "alertType") {
        return Object.entries(ALERT_TYPE_MAP).map(([value, label]) => ({
          value,
          label,
        }));
      }
      return (
        columnOption?.options?.map((opt) => ({
          value: opt,
          label: opt,
        })) || []
      );
    };

    return (
      <AlfredoStyles.TableHeader>
        <AlfredoStyles.TableHeaderRow>
          <AlfredoStyles.TableHeaderCell
            fixed
            left="0px"
            style={{ width: "calc(var(--u) * 40)" }}
          >
            <input
              style={{ cursor: "pointer", width: 20, height: 20 }}
              type="checkbox"
              checked={selectAll}
              onChange={toggleSelectAll}
            />
          </AlfredoStyles.TableHeaderCell>

          {orderedColumns.map((column) => {
            const isPinned = pinnedColumns.includes(column);
            const columnOption = columnOptions.find((c) => c.value === column);
            const left = isPinned ? `${pinnedOffsets[column]}px` : "auto";

            return (
              <AlfredoStyles.TableHeaderCell
                key={column}
                fixed={isPinned}
                left={left}
                style={{ width: `${columnWidths[column] || 250}px` }}
                /*
                 * `scope="col"` e `aria-sort` não existiam em NENHUMA tabela
                 * desta base (medido: zero ocorrências na árvore). Sem o scope,
                 * o leitor de tela não associa a célula ao cabeçalho dela e lê
                 * "Almoço" sem dizer que é a coluna Turno; sem o aria-sort, a
                 * ordenação acontece em silêncio para quem não vê a setinha.
                 */
                scope="col"
                aria-sort={
                  sortColumn === column
                    ? sortDirection === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <AlfredoStyles.CellContent>
                  <AlfredoStyles.MaterialIconsButton
                    onClick={() =>
                      setPinnedColumns((prev) =>
                        prev.includes(column)
                          ? prev.filter((c) => c !== column)
                          : [...prev, column]
                      )
                    }
                    as="button"
                    type="button"
                    aria-pressed={isPinned}
                    title={isPinned ? "Desfixar coluna" : "Fixar coluna"}
                    aria-label={
                      isPinned
                        ? `Desfixar a coluna ${columnOption?.label}`
                        : `Fixar a coluna ${columnOption?.label}`
                    }
                  >
                    {isPinned ? "lock" : "lock_open"}
                  </AlfredoStyles.MaterialIconsButton>

                  {columnOption?.label}

                  <AlfredoStyles.MaterialIconsButton
                    as="button"
                    type="button"
                    onClick={() => handleSort(column)}
                    title={`Ordenar por ${columnOption?.label}`}
                    aria-label={
                      sortColumn === column
                        ? `Ordenado por ${columnOption?.label}, ${
                            sortDirection === "asc" ? "crescente" : "decrescente"
                          }. Clique para inverter.`
                        : `Ordenar por ${columnOption?.label}`
                    }
                  >
                    {sortColumn === column
                      ? sortDirection === "asc"
                        ? "arrow_drop_up"
                        : "arrow_drop_down"
                      : "sort"}
                  </AlfredoStyles.MaterialIconsButton>

                  <AlfredoStyles.Resizer
                    onMouseDown={(e) => startColumnResize(column, e)}
                  />
                </AlfredoStyles.CellContent>
              </AlfredoStyles.TableHeaderCell>
            );
          })}

          <AlfredoStyles.TableHeaderCell
            fixed
            right="0px"
            style={{ width: "calc(var(--u) * 70)" }}
          >
            <AlfredoStyles.MaterialIconsButton
              onClick={(e) => {
                e.stopPropagation();
                setShowColumnSelector((prev) => !prev);
              }}
              title="Opções de coluna"
            >
              more_vert
            </AlfredoStyles.MaterialIconsButton>
          </AlfredoStyles.TableHeaderCell>
        </AlfredoStyles.TableHeaderRow>

        <AlfredoStyles.TableHeaderRow>
          <AlfredoStyles.TableHeaderCell
            fixed
            left="0px"
            style={{ width: "calc(var(--u) * 40)" }}
          />

          {orderedColumns.map((column) => {
            const isPinned = pinnedColumns.includes(column);
            const columnOption = columnOptions.find((c) => c.value === column);
            const left = isPinned ? `${pinnedOffsets[column]}px` : "auto";

            return (
              <AlfredoStyles.TableHeaderCell
                key={column}
                fixed={isPinned}
                left={left}
                style={{ width: `${columnWidths[column] || 250}px` }}
                /*
                 * `scope="col"` e `aria-sort` não existiam em NENHUMA tabela
                 * desta base (medido: zero ocorrências na árvore). Sem o scope,
                 * o leitor de tela não associa a célula ao cabeçalho dela e lê
                 * "Almoço" sem dizer que é a coluna Turno; sem o aria-sort, a
                 * ordenação acontece em silêncio para quem não vê a setinha.
                 */
                scope="col"
                aria-sort={
                  sortColumn === column
                    ? sortDirection === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                {columnOption?.type === "text" && (
                  <AlfredoStyles.InputFilter
                    type="text"
                    placeholder={`Filtrar ${columnOption.label}`}
                    value={filterValues[column] || ""}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [column]: e.target.value,
                      }))
                    }
                  />
                )}

                {columnOption?.type === "list" && (
                  <AlfredoStyles.DropdownFilter>
                    <select
                      value={filterValues[column] || ""}
                      onChange={(e) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [column]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      {getFilterOptions(column, columnOption).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </AlfredoStyles.DropdownFilter>
                )}

                {columnOption?.type === "date" && (
                  <AlfredoStyles.InputFilter
                    type="date"
                    value={filterValues[column] || ""}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [column]: e.target.value,
                      }))
                    }
                  />
                )}

                {columnOption?.type === "array" && (
                  <AlfredoStyles.InputFilter
                    type="text"
                    placeholder={`Filtrar ${columnOption.label}`}
                    value={filterValues[column] || ""}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [column]: e.target.value,
                      }))
                    }
                  />
                )}
              </AlfredoStyles.TableHeaderCell>
            );
          })}

          <AlfredoStyles.TableHeaderCell
            fixed
            right="0px"
            style={{ width: "calc(var(--u) * 70)" }}
          />
        </AlfredoStyles.TableHeaderRow>
      </AlfredoStyles.TableHeader>
    );
  };

  const renderTableBody = () => {
    // Quem já veio fatiado do servidor NÃO é fatiado de novo.
    //
    // Com fonte remota, `filteredData` contém apenas a página atual. Aplicar o
    // `slice((página-1)*tamanho, …)` por cima disso devolveria uma lista VAZIA
    // da página 2 em diante — a tabela apareceria em branco justamente quando
    // houvesse dado demais para caber numa página.
    const startIndex = agregadoDaTabela ? 0 : (currentPage - 1) * itemsPerPage;
    const endIndex = agregadoDaTabela
      ? filteredData.length
      : currentPage * itemsPerPage;

    // Carregando e erro só existem no modo REST; nas 7 tabelas do token as
    // props nunca chegam e o caminho é o mesmo de antes. Mesmo colSpan, para a
    // célula continuar ocupando a largura da tabela.
    if (isLoading) {
      return (
        <AlfredoStyles.TableRow>
          <AlfredoStyles.TableCell
            colSpan={orderedColumns.length + 2}
            style={{ textAlign: "center", padding: "var(--sp-40)" }}
          >
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "var(--fs-6xl)", marginBottom: "var(--sp-16)" }}
            >
              hourglass_top
            </AlfredoStyles.MaterialIcons>
            <div style={{ fontSize: "var(--fs-lg)", fontWeight: "bold" }}>
              Carregando…
            </div>
          </AlfredoStyles.TableCell>
        </AlfredoStyles.TableRow>
      );
    }

    if (error) {
      return (
        <AlfredoStyles.TableRow>
          <AlfredoStyles.TableCell
            colSpan={orderedColumns.length + 2}
            style={{ textAlign: "center", padding: "var(--sp-40)" }}
          >
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "var(--fs-6xl)", marginBottom: "var(--sp-16)" }}
            >
              error_outline
            </AlfredoStyles.MaterialIcons>
            <div style={{ fontSize: "var(--fs-lg)", fontWeight: "bold" }}>
              Não foi possível carregar
            </div>
            <div style={{ marginTop: "var(--sp-8)", color: "#666" }}>{error}</div>
          </AlfredoStyles.TableCell>
        </AlfredoStyles.TableRow>
      );
    }

    if (filteredData.length === 0) {
      return (
        <AlfredoStyles.TableRow>
          <AlfredoStyles.TableCell
            colSpan={orderedColumns.length + 2}
            style={{ textAlign: "center", padding: "var(--sp-40)" }}
          >
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "var(--fs-6xl)", marginBottom: "var(--sp-16)" }}
            >
              inventory_2
            </AlfredoStyles.MaterialIcons>
            <div style={{ fontSize: "var(--fs-lg)", fontWeight: "bold" }}>
              Nenhum dado encontrado
            </div>
            <div style={{ marginTop: "var(--sp-8)", color: "#666" }}>
              {emptyMessage ??
                (data.length === 0
                  ? "Não há registros cadastrados para esta tabela."
                  : "Nenhum registro corresponde aos filtros aplicados.")}
            </div>
          </AlfredoStyles.TableCell>
        </AlfredoStyles.TableRow>
      );
    }

    return filteredData.slice(startIndex, endIndex).map((row) => {
      const rowId = resolveRowId(row);

      return (
        <AlfredoStyles.TableRow
          key={rowId}
          isSelected={selectedRows.has(rowId)}
          onClick={() => toggleRowSelection(rowId)}
        >
          <AlfredoStyles.TableCell fixed left="0px" style={{ width: "calc(var(--u) * 40)" }}>
            <input
              style={{ cursor: "pointer", width: 20, height: 20 }}
              type="checkbox"
              checked={selectedRows.has(rowId)}
              onChange={(e) => {
                e.stopPropagation();
                toggleRowSelection(rowId);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </AlfredoStyles.TableCell>

          {orderedColumns.map((column) => {
            const isPinned = pinnedColumns.includes(column);
            const left = isPinned ? `${pinnedOffsets[column]}px` : "auto";
            const columnOption = columnOptions.find((c) => c.value === column);

            let formattedValue = row[column];

            if (columnOption?.format) {
              formattedValue = columnOption.format(formattedValue, row);
            } else if (column === "skuType") {
              formattedValue = formatProductType(formattedValue);
            } else if (
              column === "skuUnitOfMeasure" ||
              column === "unitOfMeasure"
            ) {
              formattedValue = formatUnitOfMeasure(formattedValue);
            } else if (
              column === "skuStatus" ||
              column === "supplierStatus" ||
              column === "isActive" ||
              column === "affectsStock" ||
              column === "requiresApproval"
            ) {
              formattedValue = formatStatus(formattedValue);
            } else if (column.includes("UpdatedBy")) {
              formattedValue = formatUser(formattedValue);
            } else if (
              (column.includes("CreatedAt") ||
                column.includes("UpdatedAt") ||
                column.includes("effectiveDate")) &&
              formattedValue
            ) {
              formattedValue = formatDate(formattedValue);
            } else if (column === "movementCategory") {
              formattedValue = formatMovementCategory(formattedValue);
            } else if (column === "alertType") {
              formattedValue = formatAlertType(formattedValue);
            } else if (column.includes("Price") || column.includes("price")) {
              formattedValue = formatCurrency(formattedValue);
            } else if (
              column.includes("Stock") ||
              column.includes("Quantity") ||
              column.includes("percentage")
            ) {
              formattedValue = formatNumber(formattedValue);
            }

            if (
              columnOption?.type === "array" &&
              Array.isArray(formattedValue)
            ) {
              formattedValue =
                formattedValue.length > 0
                  ? formattedValue.join(", ")
                  : "Nenhum";
            }

            if (formattedValue === undefined || formattedValue === null) {
              formattedValue = "-";
            }

            return (
              <AlfredoStyles.TableCell
                key={column}
                fixed={isPinned}
                left={left}
                style={{ width: `${columnWidths[column] || 250}px` }}
                title={
                  typeof formattedValue === "string"
                    ? formattedValue
                    : String(formattedValue)
                }
              >
                {formattedValue}
              </AlfredoStyles.TableCell>
            );
          })}

          <AlfredoStyles.TableCell fixed right="0px">
            {!hideRowActions && (
              <AlfredoStyles.Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(getUpdateModalType(), { ...row, tableType });
                }}
                edit
              >
                <AlfredoStyles.MaterialIcons>edit</AlfredoStyles.MaterialIcons>
              </AlfredoStyles.Button>
            )}
          </AlfredoStyles.TableCell>
        </AlfredoStyles.TableRow>
      );
    });
  };

  const renderPagination = () => {
    // Com fonte remota, quem sabe o tamanho da lista é o SERVIDOR.
    //
    // `filteredData.length` seria o tamanho da PÁGINA (no máximo `itemsPerPage`),
    // então `totalPages` daria 1 sempre: os botões de avançar nasceriam
    // desabilitados e ninguém chegaria na página 2 de uma lista de 300
    // fornecedores. O rodapé também mentiria a contagem.
    const totalDeItens = agregadoDaTabela
      ? suprimentos.total
      : filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalDeItens / itemsPerPage));
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const changePage = (page: number) => setCurrentPage(page);

    const handleItemsPerPageChange = (
      e: React.ChangeEvent<HTMLSelectElement>
    ) => {
      setItemsPerPage(Number(e.target.value));
      setCurrentPage(1);
    };

    const pageButtons = [];

    pageButtons.push(
      <AlfredoStyles.PaginationButton
        key="first"
        onClick={() => changePage(1)}
        disabled={currentPage === 1}
      >
        <AlfredoStyles.PaginationMaterialIcons>
          first_page
        </AlfredoStyles.PaginationMaterialIcons>
      </AlfredoStyles.PaginationButton>
    );

    pageButtons.push(
      <AlfredoStyles.PaginationButton
        key="prev"
        onClick={() => changePage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <AlfredoStyles.PaginationMaterialIcons>
          chevron_left
        </AlfredoStyles.PaginationMaterialIcons>
      </AlfredoStyles.PaginationButton>
    );

    for (let page = startPage; page <= endPage; page++) {
      pageButtons.push(
        <AlfredoStyles.PaginationButton
          key={page}
          onClick={() => changePage(page)}
          disabled={page === currentPage}
          active={page === currentPage}
        >
          {page}
        </AlfredoStyles.PaginationButton>
      );
    }

    pageButtons.push(
      <AlfredoStyles.PaginationButton
        key="next"
        onClick={() => changePage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <AlfredoStyles.PaginationMaterialIcons>
          chevron_right
        </AlfredoStyles.PaginationMaterialIcons>
      </AlfredoStyles.PaginationButton>
    );

    pageButtons.push(
      <AlfredoStyles.PaginationButton
        key="last"
        onClick={() => changePage(totalPages)}
        disabled={currentPage === totalPages}
      >
        <AlfredoStyles.PaginationMaterialIcons>
          last_page
        </AlfredoStyles.PaginationMaterialIcons>
      </AlfredoStyles.PaginationButton>
    );

    return (
      <AlfredoStyles.PaginationContainer>
        <AlfredoStyles.ItemsPerPageContainer>
          Exibir
          <AlfredoStyles.SelectItemsPerPage
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </AlfredoStyles.SelectItemsPerPage>
          itens por página
        </AlfredoStyles.ItemsPerPageContainer>

        <AlfredoStyles.PageButtonsContainer>
          {pageButtons}
        </AlfredoStyles.PageButtonsContainer>

        <AlfredoStyles.CurrentPageInfo>
          Página {currentPage} de {totalPages} | {totalDeItens} itens
        </AlfredoStyles.CurrentPageInfo>
      </AlfredoStyles.PaginationContainer>
    );
  };

  return (
    <AlfredoStyles.DynamicTableContainer
      isExpanded={isExpanded}
      ref={containerRef}
    >
      <AlfredoStyles.DynamicTableWrapper
        isLeftSidebarVisible={isLeftSidebarVisible}
        isRightSidebarVisible={isRightSidebarVisible}
        isExpanded={isExpanded}
        ref={tableWrapperRef}
      >
        {renderTableHeader()}

        {showColumnSelector && (
          <AlfredoStyles.ColumnSelectorWrapper ref={columnSelectorRef}>
            <div
              style={{
                padding: "var(--sp-10)",
                borderBottom: "1px solid #ccc",
                fontWeight: "bold",
                fontSize: "var(--fs-base)",
              }}
            >
              Colunas Visíveis
            </div>
            {columnOptions.map((column) => (
              <AlfredoStyles.ColumnSelectorItem key={column.value}>
                <AlfredoStyles.Checkbox
                  type="checkbox"
                  checked={visibleColumns.includes(column.value)}
                  onChange={() =>
                    setVisibleColumns((prev) =>
                      prev.includes(column.value)
                        ? prev.filter((v) => v !== column.value)
                        : [...prev, column.value]
                    )
                  }
                />
                {column.label}
              </AlfredoStyles.ColumnSelectorItem>
            ))}
          </AlfredoStyles.ColumnSelectorWrapper>
        )}

        <AlfredoStyles.DynamicTableScrollContainer
          isExpanded={isExpanded}
          $compacta={compacta}
          $escala={escalaDaFonte}
        >
          <AlfredoStyles.Table ref={tableRef} style={tableStyle}>
            {renderColumnHeaders()}
            <AlfredoStyles.TableBody>
              {renderTableBody()}
            </AlfredoStyles.TableBody>
          </AlfredoStyles.Table>
        </AlfredoStyles.DynamicTableScrollContainer>

        {renderPagination()}
      </AlfredoStyles.DynamicTableWrapper>
    </AlfredoStyles.DynamicTableContainer>
  );
};

export default DynamicTable;
